import { ITokenInfo } from "~/types/global"
import { axiosInstance } from "~/utils/axios"

export async function getAccountOSExecutiveSummary(
  tokenInfo: ITokenInfo,
  account_id: string,
  linkedTenantId?: number,
  duration?: string
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/executive_summary`
  const urlParams = new URLSearchParams()
  if (linkedTenantId) {
    urlParams.set("linked_tenant_id", linkedTenantId.toString())
  }
  if (urlParams.toString()) {
    url += `?${urlParams.toString()}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id,
      duration
    }
  })
}

export async function getAccountOSAccountsTracker(
  tokenInfo: ITokenInfo,
  account_id: string,
  duration: string,
  linkedTenantId?: number,
  page: number = 1,
  limit: number = 10
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/accounts_tracker`
  const params: Record<string, string | number> = {}
  if (linkedTenantId) {
    params.linked_tenant_id = linkedTenantId
  }
  const queryString = new URLSearchParams(
    params as Record<string, string>
  ).toString()
  if (queryString) {
    url += `?${queryString}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id,
      duration,
      page,
      limit
    }
  })
}

export async function getAccountOSJobOpenings(
  tokenInfo: ITokenInfo,
  account_id: string,
  duration: string,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/jobs`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id,
      duration
    }
  })
}

export async function getAccountOSPotentialPipelineIdentified(
  tokenInfo: ITokenInfo,
  account_id: string,
  duration: string,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/potential_pipeline`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id,
      duration
    }
  })
}

export async function getAccountOSOverview(
  tokenInfo: ITokenInfo,
  account_id: string,
  duration: string,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id,
      duration
    }
  })
}

export async function getAccountOSActionsOfTheDay(
  tokenInfo: ITokenInfo,
  account_id: string,
  duration: string,
  page: number = 1,
  limit: number = 10,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/actions_of_the_day`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id,
      duration,
      page,
      limit
    }
  })
}

export async function updateAccountOSActionOfTheDay(
  tokenInfo: ITokenInfo,
  action_id: string | number,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/actions_of_the_day/${action_id}`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "PATCH",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      action_id,
      status: "completed"
    }
  })
}

export async function getAccountOSSuggestedNextSteps(
  tokenInfo: ITokenInfo,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/suggested_next_steps`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {}
  })
}

export async function getAccountOSKeyIndustryTrends(
  tokenInfo: ITokenInfo,
  account_id: string,
  linkedTenantId?: number,
  industries?: string[]
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/overview/key_industry_trends`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id: account_id,
      ...(industries && industries.length > 0 ? { industries } : {})
    }
  })
}

export async function getLatestGccAnnouncement(
  tokenInfo: ITokenInfo,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/gcc-announcements/latest`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getAccountOSFinancialReports(
  tokenInfo: ITokenInfo,
  account_id: string,
  duration: string,
  page: number = 1,
  limit: number = 10,
  linkedTenantId?: number,
  // When true the backend skips presigning each report's S3 PDF URL — the
  // leadership briefing fan-out links to the account page, not the PDF, so it
  // avoids ~N presign round-trips per page.
  skip_s3: boolean = false
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/financial_reports`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      account_id,
      duration,
      page,
      limit,
      skip_s3
    }
  })
}
